import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateProject } from '../../api/projects';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { CopyButton } from '../../components/CopyButton';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { toSlug } from '../../utils/format';
import shared from '../shared.module.css';
import styles from './ProjectCreatePage.module.css';

interface NewProjectForm {
	name: string;
	slug: string;
	origins: string;
}

export function ProjectCreatePage() {
	const navigate = useNavigate();
	const createProject = useCreateProject();

	const [form, setForm] = useState<NewProjectForm>({ name: '', slug: '', origins: '' });
	const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
	const [createdKeys, setCreatedKeys] = useState<{ client_key: string; server_key: string } | null>(
		null,
	);

	function handleNameChange(name: string) {
		setForm((current) => ({
			...current,
			name,
			slug: slugManuallyEdited ? current.slug : toSlug(name),
		}));
	}

	function handleSlugChange(slug: string) {
		setSlugManuallyEdited(true);
		setForm((current) => ({ ...current, slug }));
	}

	function handleCancel() {
		setForm({ name: '', slug: '', origins: '' });
		setSlugManuallyEdited(false);
		setCreatedKeys(null);
		createProject.reset();
		navigate('/projects');
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();

		const allowed_origins = form.origins
			.split(',')
			.map((value) => value.trim())
			.filter(Boolean);

		try {
			const result = await createProject.mutateAsync({
				name: form.name.trim(),
				slug: form.slug.trim(),
				allowed_origins,
			});
			setCreatedKeys({ client_key: result.client_key, server_key: result.server_key });
			setForm({ name: '', slug: '', origins: '' });
			setSlugManuallyEdited(false);
		} catch {
			// error displayed from createProject.error
		}
	}

	return (
		<div>
			<PageHeader
				title="New Project"
				breadcrumbs={[{ label: 'Projects', href: '/projects' }, { label: 'New' }]}
			/>

			<div className={shared.content}>
				<Card className={styles.formCard}>
					{createdKeys ? (
						<div className={styles.successPanel}>
							<p className={styles.successMessage}>
								Project created! Save these keys — the full values are only shown once.
							</p>
							<div className={styles.keyRow}>
								<span className={styles.keyLabel}>Client key</span>
								<span className={styles.keyValue}>
									<CopyButton text={createdKeys.client_key} displayText={createdKeys.client_key} />
								</span>
							</div>
							<div className={styles.keyRow}>
								<span className={styles.keyLabel}>Server key</span>
								<span className={styles.keyValue}>
									<CopyButton text={createdKeys.server_key} displayText={createdKeys.server_key} />
								</span>
							</div>
							<div className={shared.formActions}>
								<Button variant="secondary" onClick={handleCancel}>
									Done
								</Button>
							</div>
						</div>
					) : (
						<form onSubmit={handleSubmit} className={shared.form}>
							<Input
								label="Name"
								name="project-name"
								value={form.name}
								onChange={handleNameChange}
								required
								placeholder="My Project"
							/>
							<Input
								label="Slug"
								name="project-slug"
								value={form.slug}
								onChange={handleSlugChange}
								required
								mono
								placeholder="my-project"
							/>
							<Input
								label="Allowed Origins"
								name="project-origins"
								value={form.origins}
								onChange={(value) => setForm((current) => ({ ...current, origins: value }))}
								placeholder="https://example.com, https://staging.example.com"
							/>
							{createProject.error && (
								<p className={shared.formError}>
									{createProject.error instanceof Error
										? createProject.error.message
										: 'Failed to create project'}
								</p>
							)}
							<div className={shared.formActions}>
								<Button variant="secondary" type="button" onClick={handleCancel}>
									Cancel
								</Button>
								<Button
									variant="primary"
									type="submit"
									loading={createProject.isPending}
									disabled={!form.name.trim() || !form.slug.trim()}
								>
									Create Project
								</Button>
							</div>
						</form>
					)}
				</Card>
			</div>
		</div>
	);
}
